import { useState, useEffect, useMemo, FormEvent } from 'react';
import { useSession } from '@/auth/useSession';
import { useUIStore } from '@/state/useUIStore';
import type { ShipType } from '@/net/protocol';
import './Home.css';

interface ClassData {
  type: ShipType;
  name: string;
  description: string;
}

const CLASSES: ClassData[] = [
  { type: 'scout', name: 'Ranger', description: 'Fast and deadly at range.' },
  { type: 'fighter', name: 'Knight', description: 'Balanced warrior. Versatile in combat.' },
  { type: 'cruiser', name: 'Paladin', description: 'Heavy armor. Hard to bring down.' },
  { type: 'destroyer', name: 'Mage', description: 'Devastating power from afar.' },
];

const RUNIC_MAP: Record<string, string> = {
  V: '\u16B9',
  A: '\u16A8',
  G: '\u16B7',
  R: '\u16B1',
  I: '\u16C1',
  O: '\u16DF',
};

const RUNES = [
  '\u16A0','\u16A2','\u16A6','\u16A8','\u16B1','\u16B7','\u16B9','\u16BA',
  '\u16BE','\u16C1','\u16C3','\u16C7','\u16C8','\u16C9','\u16CA','\u16CB',
  '\u16CF','\u16D2','\u16D6','\u16D7','\u16DA','\u16DC','\u16DE','\u16DF',
];

const RUNE_COUNT = 70;

function seededRandom(seed: number) {
  let s = seed;
  return () => { s = (s * 16807 + 0) % 2147483647; return s / 2147483647; };
}

/** Place a rune in the margins, avoiding the center content area */
function edgePosition(rand: () => number): { x: number; y: number } {
  // Pick which edge band: 0=left, 1=right, 2=top, 3=bottom
  const edge = Math.floor(rand() * 4);
  switch (edge) {
    case 0: return { x: rand() * 16, y: rand() * 100 };         // left strip
    case 1: return { x: 84 + rand() * 16, y: rand() * 100 };    // right strip
    case 2: return { x: 16 + rand() * 68, y: rand() * 12 };     // top strip (between left/right)
    default: return { x: 16 + rand() * 68, y: 88 + rand() * 12 }; // bottom strip
  }
}

interface RuneGlyph {
  char: string;
  x: number;
  y: number;
  size: number;
  rotation: number;
}

function RuneBackground() {
  const glyphs = useMemo<RuneGlyph[]>(() => {
    const rand = seededRandom(42);
    return Array.from({ length: RUNE_COUNT }, () => {
      const pos = edgePosition(rand);
      return {
        char: RUNES[Math.floor(rand() * RUNES.length)],
        x: pos.x,
        y: pos.y,
        size: 16 + rand() * 28,
        rotation: -25 + rand() * 50,
      };
    });
  }, []);

  const [visible, setVisible] = useState<boolean[]>(() => new Array(RUNE_COUNT).fill(false));

  useEffect(() => {
    let alive = true;
    const timers: ReturnType<typeof setTimeout>[] = [];

    function cycle(idx: number) {
      if (!alive) return;
      // off period: 200–800ms
      const off = 200 + Math.random() * 600;
      const t1 = setTimeout(() => {
        if (!alive) return;
        setVisible(prev => { const n = [...prev]; n[idx] = true; return n; });
        // on period: 300–1000ms
        const on = 300 + Math.random() * 700;
        const t2 = setTimeout(() => {
          if (!alive) return;
          setVisible(prev => { const n = [...prev]; n[idx] = false; return n; });
          cycle(idx);
        }, on);
        timers.push(t2);
      }, off);
      timers.push(t1);
    }

    // stagger initial starts over the first 2s
    for (let i = 0; i < RUNE_COUNT; i++) {
      const t = setTimeout(() => cycle(i), Math.random() * 2000);
      timers.push(t);
    }

    return () => { alive = false; timers.forEach(clearTimeout); };
  }, []);

  return (
    <div className="rune-bg" aria-hidden="true">
      {glyphs.map((g, i) => (
        <span
          key={i}
          className={`rune-bg-glyph ${visible[i] ? 'visible' : ''}`}
          style={{
            left: `${g.x}%`,
            top: `${g.y}%`,
            fontSize: `${g.size}px`,
            transform: `rotate(${g.rotation}deg)`,
          }}
        >
          {g.char}
        </span>
      ))}
    </div>
  );
}

const TITLE = 'VAGARIO';

function AnimatedTitle() {
  const [flipped, setFlipped] = useState<boolean[]>(new Array(TITLE.length).fill(false));

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    function scheduleFlip() {
      const idx = Math.floor(Math.random() * TITLE.length);
      const delay = 800 + Math.random() * 2000;
      const t = setTimeout(() => {
        setFlipped((prev) => {
          const next = [...prev];
          next[idx] = !next[idx];
          return next;
        });
        scheduleFlip();
      }, delay);
      timers.push(t);
    }

    for (let i = 0; i < TITLE.length; i++) {
      const t = setTimeout(() => {
        setFlipped((prev) => { const next = [...prev]; next[i] = true; return next; });
      }, 2000 + i * 300);
      timers.push(t);
    }

    for (let i = 0; i < TITLE.length; i++) {
      const t = setTimeout(() => {
        setFlipped((prev) => { const next = [...prev]; next[i] = false; return next; });
      }, 5000 + i * 200);
      timers.push(t);
    }

    const startRandom = setTimeout(() => scheduleFlip(), 6500);
    timers.push(startRandom);

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <h1 className="home-title">
      {TITLE.split('').map((ch, i) => (
        <span key={i} className={`title-letter ${flipped[i] ? 'runic' : ''}`}>
          <span className="title-letter-latin">{ch}</span>
          <span className="title-letter-rune">{RUNIC_MAP[ch] ?? ch}</span>
        </span>
      ))}
    </h1>
  );
}

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const { login, register, loading } = useSession();
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) { setError('Please fill in all fields'); return; }
    if (isRegistering && password !== confirmPassword) { setError('Passwords do not match'); return; }
    try {
      if (isRegistering) {
        await register(email, password);
        setError('Check your email for verification link');
      } else {
        await login(email, password);
        onSuccess();
      }
    } catch (err) { setError((err as Error).message); }
  };

  return (
    <div className="home-login">
      <h2 className="home-login-title">
        {isRegistering ? 'Create Account' : 'Sign In'}
      </h2>
      <form className="home-login-form" onSubmit={handleSubmit}>
        <label>
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" disabled={loading} autoComplete="email" />
        </label>
        <label>
          <span>Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" disabled={loading}
            autoComplete={isRegistering ? 'new-password' : 'current-password'} />
        </label>
        {isRegistering && (
          <label>
            <span>Confirm Password</span>
            <input type="password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••" disabled={loading} autoComplete="new-password" />
          </label>
        )}
        {error && <div className="home-login-error">{error}</div>}
        <button type="submit" className="home-login-submit" disabled={loading}>
          {loading ? 'Loading...' : isRegistering ? 'Create Account' : 'Sign In'}
        </button>
      </form>
      <button type="button" className="home-login-toggle"
        onClick={() => { setIsRegistering(!isRegistering); setError(null); }} disabled={loading}>
        {isRegistering ? 'Already have an account? Sign in' : 'Need an account? Register'}
      </button>
    </div>
  );
}

export function Home() {
  const { user, logout } = useSession();
  const { selectedShipType, setSelectedShipType, setScreen, openShop, openInventory, openSettings } = useUIStore();
  const [showLogin, setShowLogin] = useState(false);

  return (
    <div className="home">
      <RuneBackground />
      {/* Account — top right */}
      <div className="home-account">
        {user ? (
          <>
            <span className="home-email">{user.email}</span>
            <button className="engraved-link" onClick={() => logout()}>Logout</button>
          </>
        ) : (
          <button className="engraved-link" onClick={() => setShowLogin(!showLogin)}>
            {showLogin ? 'Close' : 'Sign In'}
          </button>
        )}
      </div>

      {showLogin && !user && (
        <LoginForm onSuccess={() => setShowLogin(false)} />
      )}

      <main className="home-hero">
        <AnimatedTitle />
        <p className="home-tagline">to wander</p>

        <div className="home-rule" />

        <section className="home-classes">
          <h2 className="home-section-title">Choose Your Path</h2>
          <div className="home-class-grid">
            {CLASSES.map((cls) => (
              <button
                key={cls.type}
                className={`home-class-card ${selectedShipType === cls.type ? 'active' : ''}`}
                onClick={() => setSelectedShipType(cls.type)}
              >
                <span className="home-class-name">{cls.name}</span>
                <span className="home-class-desc">{cls.description}</span>
              </button>
            ))}
          </div>
        </section>

        <button className="home-play" onClick={() => setScreen('match')}>
          Enter World
        </button>
      </main>

      <nav className="home-nav">
        <button className="engraved-link" onClick={() => openShop()}>Armory</button>
        <button className="engraved-link" onClick={() => openInventory()}>Inventory</button>
        <button className="engraved-link" onClick={() => openSettings()}>Settings</button>
      </nav>

      <footer className="home-footer">&copy; 2026 Vagario</footer>
    </div>
  );
}
